/**
 * Copyright 2015-present Singlepoint. All Rights Reserved.
 *
 * @flow
 */

import '../../styles/commonPageStyles.scss';
import '../../styles/commonFormStyles.scss';
import './ViewQuote.scss';

import Auth from '@aws-amplify/auth';
import {
  AccordionText,
  ButtonGroup,
  Checkbox,
  DatePicker,
  InputField,
  SimpleSwitch,
  TitleWithUnderLine,
  WideDivider
} from '@boi/core/lib';
import { differenceInCalendarDays } from 'date-fns';
import { Form, FormikProps, withFormik } from 'formik';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Location, RouterHistory } from 'react-router-dom';

import { config, customer_config } from '../../amplify.config';
import { CognitoContext } from '../../CognitoUtils';
import { AgentScript } from '../../components/AgentScript/AgentScript';
import { ExternalLink } from '../../components/ExternalLink/ExternalLink';
import TermsAndConditionsCheckbox
  from '../../components/HomeTermsAndConditions/TermsAndConditionsCheckbox/TermsAndConditionsCheckbox';
import { MobileBackButton } from '../../components/MobileBackButton';
import { HomePremiumBreakdown } from '../../components/PremiumBreakdowns';
import { PropertyMapper } from '../../components/PremiumBreakdowns/HomePremiumBreakdown/AmountMapper';
import ProgressBar from '../../components/ProgressBar';
import QuoteDisplay from '../../components/QuoteDisplay';
import QuoteExtras from '../../components/QuoteExtras';
import QuoteInfo from '../../components/QuoteInfo';
import {
  commonFormStylesIdentifier,
  commonPageStylesIdentifier,
  DIRECT_DEBIT_STORAGE,
  HOME_QUOTE_RECALCULATED_VALUES_STORAGE,
  HOME_QUOTE_STORAGE,
  HOME_QUOTE_THANK_YOU_STORAGE,
  REQUEST_FAILURE,
  REQUEST_LOADING,
  REQUEST_SUCCESS,
  SELECTED_HOME_QUOTE_STORAGE,
  TOGGLE_OPTIONAL_EXTRAS
} from '../../constants';
import { dividerColor } from '../../constants/colors';
import { policyImportantDocuments } from '../../constants/externalLinks';
import {
  DISPLAY_NUM_OF_FEATURES_VIEW_QUOTE,
  EXCESS,
  EXCESS_AMOUNT_MAPPER,
  FORCE_LOGIN,
  HOME_COVER_TYPE,
  INSURER_TYPE
} from '../../constants/home';
import {
  insurSmartCheckText,
  insurSmartErrorMsg,
  insurSmartProcced,
  insurSmartPtFive,
  insurSmartPtFour,
  insurSmartPtOne,
  insurSmartPtThree,
  insurSmartPtTwo,
  insurSmartStartDateChange,
  insurSmartTitle
} from '../../constants/homeInsurSmart/insurSmartConstants';
import {
  ABOUT_YOU,
  CODE_VALUES,
  HOME_INSURANCE_TYPE,
  premiumBreakDownConstants
} from '../../constants/insuranceTypeConstants';
import {
  HOME_JOURNEY,
  JOURNEY_YOUR_QUOTE,
  RENEWAL_JOURNEY_LABEL_TEXT,
  STAGES_HOME_JOURNEY,
  STAGES_RENEWAL_JOURNEY
} from '../../constants/journey';
import {
  ALTERNATIVE_POLICY,
  LAST_PAGE_NAME,
  POLICY_RENEWAL
} from '../../constants/sessionStorage/genericStorageIdentifiers';
import { AGENT_SUMMARY_QUOTE_STORAGE } from '../../constants/sessionStorage/homeStorageIdentifiers';
import { CommonPageStylesDesktopBackButton } from '../../constants/styleConstants/commonClassNames';
import {
  getObjectFromSessionStorage,
  insurerCaption,
  isAgent,
  makeRequest,
  removeFromSessionStorage,
  saveInSessionStorage
} from '../../helpers';
import { addDaysToDate, getCurrentDate } from '../../helpers/DateHelper';
import { mapGetQuote } from '../../mappings/home/MapGetQuote';
import { routes } from '../../routes';
import { postHomeQuote } from '../../services/homeServices';
import { getUsers } from '../../services/retriveUser';
import type { HomePayloadType, Insurer, PayloadType, RequestStateType } from '../../types';
import type { QuoteFeature } from '../../types/Home';
import UserContext from '../../UserContext';
import InsuranceCodes from './InsuranceCodes';
import { InsurSmartScriptContent, ScriptContent1, ScriptContent2, ScriptContent3 } from './ScriptContent';
import ViewQuoteLogo from './ViewQuoteLogo';
import { viewQuoteYupSchema } from './viewQuoteYupSchema';

interface Props {
  location: Location;
  history: RouterHistory;
}

export interface ViewQuoteValues {
  accidentalDamage: boolean;
  ncdProtectionPlus: boolean;
  homeEmergencyAssist: boolean;
  excess: number;
  mortgageApplicationNumber: ?string;
}

type QuoteData = {
  yourHomeObj?: {
    coverType: string
  }
};

type InsurSmartRequestObject = {
  mortgage_reference: string,
  quote_reference: string
};

type UpdateStatusRequestObject = {
  quote_reference: string
};

const insurSmartTsAndCs = (
  <ul className={`insur_smart_terms`}>
    <li>{insurSmartPtOne}</li>
    <li>{insurSmartPtTwo}</li>
    <li>{insurSmartPtThree}</li>
    <li>{insurSmartPtFour}</li>
    <li>{insurSmartPtFive}</li>
  </ul>
);

const basePath = process.env.REACT_APP_API_ENDPOINT || '/';

const INSUR_SMART_ENDPOINT = '/itb';
const HOME_ENDPOINT = '/applied_home_quotes';

function getInsurer(insurerType: string): Insurer {
  return insurerType.toLowerCase().includes('rsa') ? 'RSA' : 'FBD';
}

// eslint-disable-next-line complexity
const ViewQuote = (props: Props & FormikProps<ViewQuoteValues>) => {
  const {
    errors,
    handleBlur,
    handleChange,
    isValid,
    history,
    setFieldValue,
    setTouched,
    touched,
    values,
    match
  } = props;

  const className = 'c-ViewQuote';
  const [buttonType, setButtonType] = useState('proceed');
  const [price, setPrice] = useState(0);
  const [monthlyPrice, setMonthlyPrice] = useState(0);
  const [quoteReference, setQuoteReference] = useState(0);
  const [premiumDetails, setPremiumDetails] = useState([]);
  const [insurerType, setInsurerType] = useState('');
  const [alternativePolicy, setAlternativePolicy] = useState(false);
  const [togglesState, setToggleState] = useState({
    accidentalDamage: props.values.accidentalDamage,
    ncdProtectionPlus: props.values.ncdProtectionPlus,
    homeEmergencyAssist: props.values.homeEmergencyAssist,
    excess: props.values.excess
  });
  const [addonDetails, setAddonDetails] = useState([]);
  const [coverDetails, setCoverDetails] = useState({
    rebuildCost: 0,
    contentsCoverOf: 0,
    specifiedItems: [],
    unspecifiedItemsValue: 0,
    excess: '',
    coverType: '',
    start_date: ''
  });
  const [claims, setClaims] = useState();
  const isMounted = useRef(false);
  const FULL_DIRECT_DEBIT_STORAGE_IDENTIFIER = `${HOME_INSURANCE_TYPE}${DIRECT_DEBIT_STORAGE}`;
  const insuranceType = match.params.type;
  const bindingQuote = getObjectFromSessionStorage(`${insuranceType}Quote`);
  const [insurSmart, setInsurSmart] = useState(false);
  const [insurSmartChecked, setInsurSmartChecked] = useState(false);
  const [insurSmartDisplayError, setInsurSmartDisplayError] = useState(
    false
  );
  const [insurSmartQuote, setInsurSmartQuote] = useState(false);

  const [requestStatus, setRequestStatus] = useState<RequestStateType>(
    REQUEST_LOADING
  );

  const { feature, cognitoUser } = useContext(CognitoContext);
  const { agentScriptPending, setAgentScriptPending } = useContext(UserContext);
  const [ insurSmartAgentScriptViewed, setInsurSmartAgentScriptViewed ] = useState(false);

  removeFromSessionStorage(FULL_DIRECT_DEBIT_STORAGE_IDENTIFIER);

  let payload: PayloadType = {};

  const checkToggleState = () => {
    return (
      togglesState.accidentalDamage === props.values.accidentalDamage &&
      togglesState.ncdProtectionPlus === props.values.ncdProtectionPlus &&
      togglesState.homeEmergencyAssist === props.values.homeEmergencyAssist &&
      togglesState.excess === props.values.excess
    );
  };

  // eslint-disable-next-line complexity
  useEffect(() => {
    const quoteData = getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE);
    const renewalFlag =
      getObjectFromSessionStorage(POLICY_RENEWAL)?.renewal || false;
    setFieldValue('renewal', renewalFlag);
    const stringOnly = (val: any) => (val ? val : '');
    let element = {};
    if (quoteData) {
      element = getObjectFromSessionStorage(TOGGLE_OPTIONAL_EXTRAS) || {};
      if (element && Object.keys(element).length > 0) {
        setToggleState(element);
      }
      setPremiumDetails(quoteData.premium_details);
      setQuoteReference(quoteData.quote_reference);
      setInsurerType(stringOnly(quoteData.insurer));
      const alternatePolicy = quoteData.current_insurer_renewal_quote === false;
      setAlternativePolicy(alternatePolicy);
      saveInSessionStorage(ALTERNATIVE_POLICY, alternatePolicy);

      setAddonDetails(quoteData.addon_details || []);
      if (renewalFlag) {
        setCoverDetails({
          rebuildCost: quoteData.cover_details_information.buildings_amount,
          contentsCoverOf: quoteData.cover_details_information.contents_amount,
          specifiedItems: quoteData.cover_details_information.specified_items,
          unspecifiedItemsValue:
          quoteData.cover_details_information
            .unspecified_personal_items_amount,
          excess: quoteData.cover_details_information.excess_amount,
          coverType:
          quoteData.cover_details_information.risk_address.dwelling_type,
          start_date: quoteData.cover_details_information.start_date
        });
        setClaims(quoteData?.cover_details_information?.claims_details);
      }
      const inclevyPrice = getQuoteTotal(
        quoteData.premium_details,
        quoteData.insurer
      );
      setPrice(inclevyPrice);
      setMonthlyPrice(inclevyPrice / 12);
      setFieldValue('mortgageApplicationNumber', quoteData.mortgage_reference);
    } else {
      setPrice(0);
      setMonthlyPrice(0);
      setPremiumDetails([]);
      setQuoteReference(0);
      setInsurerType('');
    }
    if (element?.insurerType === quoteData.insurer) {
      Object.entries(element).forEach(([key, value]: [string, mixed]) => {
        setFieldValue(key, value);
      });
    }
    if (
      [HOME_COVER_TYPE.OWN_LIVE, HOME_COVER_TYPE.RENT].includes(
        yourHomeObj.coverType
      ) &&
      element?.accidentalDamage === undefined
    )
      setFieldValue('accidentalDamage', false);
    const quoteValues = getObjectFromSessionStorage(
      HOME_QUOTE_THANK_YOU_STORAGE
    );
    if (feature?.flags?.insure_smart_enabled === true && quoteValues.yourCoverObj?.itb) {
      setInsurSmart(true);
      setAgentScriptPending(false)
      setInsurSmartAgentScriptViewed(true);
      setInsurSmartQuote(true);
      setFieldValue('insurSmartSwitch', true);
    }
    
  }, []);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (isMounted.current) {
      if (checkToggleState()) {
        setButtonType('proceed');
      } else {
        setButtonType('recalculate');
      }
    } else {
      isMounted.current = true;
    }
  }, [
    props.values.accidentalDamage,
    props.values.ncdProtectionPlus,
    props.values.homeEmergencyAssist,
    props.values.excess
  ]);

  useEffect(() => {
    if (requestStatus === REQUEST_FAILURE) {
      props.history.push(
        `${routes.genericErrorPage.url}${HOME_INSURANCE_TYPE}`
      );
    }
  }, [requestStatus, props.history]);

  const getYourHomeObject = (quoteData: QuoteData) =>
    quoteData.yourHomeObj || {};

  let yourCoverObj = {};
  const agentSummaryQuote =  getObjectFromSessionStorage(AGENT_SUMMARY_QUOTE_STORAGE);
  const allData = getObjectFromSessionStorage(agentSummaryQuote ? HOME_QUOTE_THANK_YOU_STORAGE : HOME_QUOTE_STORAGE);
  allData.yourCoverObj
    ? (yourCoverObj = allData.yourCoverObj)
    : (yourCoverObj = {});
  const yourHomeObj = getYourHomeObject(allData);

  const renderHomeTypeLinks = () => (
    <>
      Important Documents{' '}
      <ExternalLink url={policyImportantDocuments} text="Click here" />
    </>
  );

  const fbdAndRsaFeatures: QuoteFeature[] = [
    { label: () => 'Pay by direct debit at no extra cost', tick: true },
    { label: () => '24 hour helpline in a home emergency ', tick: true },
    { label: () => 'Loss of keys/lock replacement', tick: true },
    { label: () => 'Fire brigade charges, up to €2,500', tick: true },
    {
      label: () => renderHomeTypeLinks(),
      tick: true
    }
  ];

  const fbdAndRsaFeaturesRenewal: QuoteFeature[] = [
    { label: () => 'Review your policy summary below', tick: false },
    { label: () => 'Review and accept all Terms & Conditions', tick: false },
    { label: () => 'Select your preferred Payment Method', tick: false },
    {
      label: () => renderHomeTypeLinks(),
      tick: false
    }
  ];

  // eslint-disable-next-line complexity
  const checkResponse = (payload: HomePayloadType[]) => {
    try {
      if (Array.isArray(payload) && payload.length > 0) {
        setRequestStatus(REQUEST_SUCCESS);
        setPremiumDetails(payload[0].premium_details);
        setQuoteReference(payload[0].quote_reference);
        setAddonDetails(payload[0].addon_details || []);
        setButtonType('proceed');
        const inclevyPrice = getQuoteTotal(
          payload[0].premium_details,
          payload[0].insurer
        );
        setPrice(inclevyPrice);
        setMonthlyPrice(inclevyPrice / 12);
        const quoteData = payload[0];
        if (quoteData) {
          saveInSessionStorage(
            SELECTED_HOME_QUOTE_STORAGE,
            JSON.stringify(quoteData)
          );
        }
        const tempToggleState = {
          accidentalDamage: props.values.accidentalDamage,
          ncdProtectionPlus: props.values.ncdProtectionPlus,
          homeEmergencyAssist: props.values.homeEmergencyAssist,
          excess: props.values.excess,
          insurerType: payload[0].insurer
        };
        setToggleState(tempToggleState);
        saveInSessionStorage(
          TOGGLE_OPTIONAL_EXTRAS,
          JSON.stringify(tempToggleState)
        );
      } else {
        setRequestStatus(REQUEST_FAILURE);
      }
    } catch {
      setRequestStatus(REQUEST_FAILURE);
    }
  };
  const recalculateHomeQuote = async () => {
    const quoteData = getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE);
    const quoteValues = getObjectFromSessionStorage(
      HOME_QUOTE_THANK_YOU_STORAGE
    ); // previous mapping of payload

    const previousMapping = mapGetQuote(
      quoteValues.aboutYouObj,
      quoteValues.yourHomeObj,
      quoteValues.yourCoverObj,
      quoteValues.assumptions,
      quoteValues.extraQuestionsValues
    );
    const homeQuoteValues = props.values;
    setRequestStatus(REQUEST_LOADING);

    const newCoverDetails = Object.assign(
      {},
      previousMapping.home_quote.cover_details,
      {
        excess_amount: Number(EXCESS_AMOUNT_MAPPER[homeQuoteValues.excess]),
        contents_accidental_damage_cover: homeQuoteValues.accidentalDamage,
        no_claim_discount_cover: homeQuoteValues.ncdProtectionPlus,
        home_emergency_assist_cover: homeQuoteValues.homeEmergencyAssist,
        premium: price
      }
    );

    const newMapping = { ...previousMapping };
    newMapping.home_quote.cover_details = newCoverDetails;
    newMapping.home_quote['product_selected'] = quoteData.scheme_code;
    newMapping.home_quote['quote_reference'] = quoteData.quote_reference;
    payload = await postHomeQuote(newMapping, props);

    if (payload) {
      checkResponse(payload);
    } else {
      setRequestStatus(REQUEST_FAILURE);
    }
  };

  const handleChangeWRecalculate = (
    fieldName: string,
    value: string | number
  ) => {
    setFieldValue(fieldName, value);
    const data = getObjectFromSessionStorage(
      HOME_QUOTE_RECALCULATED_VALUES_STORAGE
    );
    data[fieldName] = value;
    saveInSessionStorage(
      HOME_QUOTE_RECALCULATED_VALUES_STORAGE,
      JSON.stringify(data)
    );
  };

  const isFieldError = (status: string) =>
    touched[status] && errors[status] !== undefined;

  const recalculatedExcess = getObjectFromSessionStorage(
    HOME_QUOTE_RECALCULATED_VALUES_STORAGE
  ).excess;

  const journey = (
    quoteData: any,
    userExistOrLoggedIn: any,
    isAgent: boolean
  ) => {
    if (isAgent) {
      Auth.configure(config);
    }
    if (userExistOrLoggedIn) {
      history.push(`${routes.makePayment.url}${insuranceType}`);
    } else {
      saveInSessionStorage(
        FORCE_LOGIN,
        JSON.stringify({ forceLogIn: true, insuranceType: insuranceType })
      );
      history.push(
        !isAgent
          ? `${routes.forceLogin.url}${insuranceType}`
          : `${routes.signUp.url}`
      );
    }
  };

  const goToNextPage = (quoteData: any) => {
    if (isAgent()) {
      Auth.configure(customer_config);
      getUsers({
        dropdownValue: 'email',
        searchInput: bindingQuote?.aboutYouObj?.email || bindingQuote?.aboutYou?.email || ''
      })
        .then((res: any) => {
          if (res?.user_list?.length > 0) {
            journey(quoteData, true, true);
          } else {
            journey(quoteData, false, true);
          }
        })
        .catch((e: any) => {
          console.error(e);
        });
    } else {
      journey(quoteData, cognitoUser, false);
    }
  };

  const isAlternativePolicy = (altPolicy: boolean): boolean => {
    return altPolicy === true;
  };

  const singlePaymentPolicy = (
    autoRenewal: boolean,
    direct_debit_policy: boolean
  ): boolean => {
    return autoRenewal === false || direct_debit_policy === false;
  };

  const isAutoRenewal = (autoRenewal: boolean): boolean => {
    return autoRenewal === true;
  };

  const marketingDisabled = (marketingEnabled: boolean): boolean => {
    return marketingEnabled === false;
  };

  async function postInsurSmartRequest(values: ViewQuoteValues) {
    const homeQuoteSelected = getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE);
    const mortgageApNum: string = values?.mortgageApplicationNumber ?? '';
    const quoteReference: string = homeQuoteSelected?.quote_reference ?? '';

    const insurSmartRequest: InsurSmartRequestObject = {
      mortgage_reference: mortgageApNum,
      quote_reference: quoteReference
    };

    const insurSmartRegistrationResponse = await makeRequest({
      url: `${basePath}/home${INSUR_SMART_ENDPOINT}/register`, method: 'POST', payload: insurSmartRequest
    });

    //log inplace until summary page made to navigate to
    console.log(JSON.stringify(insurSmartRegistrationResponse));
  }

  // eslint-disable-next-line complexity
  const onProceedClicked = () => {
    const quoteData = { price, quoteReference, monthlyPrice, insurerType, insurSmartQuote: (insurSmartQuote && insurSmart) };

    const selectedQuoteData = getObjectFromSessionStorage(
      SELECTED_HOME_QUOTE_STORAGE
    );

    saveInSessionStorage(
      FULL_DIRECT_DEBIT_STORAGE_IDENTIFIER,
      JSON.stringify(quoteData)
    );

    const marketingEnabled = feature.flags?.marketing_enabled || false;

    if (insurSmart && !insurSmartChecked) {
      setInsurSmartDisplayError(true);
    } else {
      if (insurSmartChecked) {
        if (!insurSmartQuote) {
          postInsurSmartRequest(values);
        }
        history.push({ pathname: `${routes.insurSmartConfirmation.url}` });
      } else if (isAlternativePolicy(alternativePolicy)) {
        history.push({
          pathname: `${routes.renewalMakePayment.url}${HOME_INSURANCE_TYPE}`,
          state: quoteData
        });
      } else if (
        singlePaymentPolicy(
          values.autoRenewal,
          selectedQuoteData?.direct_debit_policy
        )
      ) {
        history.push({
          pathname: `${routes.renewalMakePayment.url}${HOME_INSURANCE_TYPE}`,
          state: quoteData
        });
      } else if (isAutoRenewal(values.autoRenewal)) {
        history.push({
          pathname: `${routes.paymentThankYou.url}${HOME_INSURANCE_TYPE}`,
          state: {
            insuranceType: HOME_INSURANCE_TYPE
          }
        });
      } else if (marketingDisabled(marketingEnabled)) {
        goToNextPage(quoteData);
      } else {
        history.push({
          pathname: `${routes.marketingConsent.url}${HOME_INSURANCE_TYPE}`,
          state: quoteData
        });
      }
      if(insurSmartTrue() && !insurSmartQuote){
        callLambdaApi();
      }
    }
  };

  const getQuoteExtras = () => {
    return (
      <div>
        <QuoteExtras
          errors={errors}
          handleBlur={handleBlur}
          setFieldValue={setFieldValue}
          setTouched={setTouched}
          handleChangeWRecalculate={handleChangeWRecalculate}
          touched={touched}
          values={values}
          handleChange={handleChange}
          isFieldError={isFieldError}
          addonDetails={addonDetails || []}
        />
      </div>
    );
  };

  const checkIfPolicyExcessIsAllowed = () => {
    return [
      HOME_COVER_TYPE.OWNUNOCCUPIED,
      HOME_COVER_TYPE.OWNCONSTRUCTION,
      HOME_COVER_TYPE.OWNRENOVATION
    ].includes(yourHomeObj.coverType);
  };

  const getCompanyName = () =>
    insurerType === INSURER_TYPE.FBD
      ? 'FBD Insurance plc'
      : 'Intact Insurance Ireland DAC';

  // eslint-disable-next-line complexity
  const getQuoteTotal = useCallback((quoteData: any[], insurer: string) => {
    const insuranceProvider = insurerType !== '' ? insurerType : insurer;
    const premDetails = quoteData;
    if (premDetails && premDetails.length > 0) {
      const element = premDetails.find((item: any) => {
        if (item.code === CODE_VALUES[insuranceProvider]) {
          return item;
        }
      });
      if (element) {
        const propertyIdentifier =
          PropertyMapper[insuranceProvider][
            premiumBreakDownConstants.INCLEVY
            ] === 'AMT'
            ? 'amount'
            : 'running_total';
        return element[propertyIdentifier] ? element[propertyIdentifier] : 0;
      } else {
        return 0;
      }
    } else {
      return 0;
    }
  }, []);

  const getQuoteInfoProps = () => {
    if (values.renewal) {
      return {
        showReadMoreOption: false,
        showUpToFeature: DISPLAY_NUM_OF_FEATURES_VIEW_QUOTE,
        quoteFeatures: fbdAndRsaFeaturesRenewal
      };
    } else {
      return {
        quoteFeatures: fbdAndRsaFeatures
      };
    }
  };

  function insurSmartTrue(): boolean {
    return isAgent() && insurSmart;
  }

  // eslint-disable-next-line flowtype/require-return-type
  function agentScriptingText() {
    return insurSmart ? <AgentScript><InsurSmartScriptContent /></AgentScript>
      : <AgentScript>
        <ScriptContent1 insurer={insurerType} />
        <ScriptContent2 />
        <ScriptContent3 />
      </AgentScript>;
  }

  const storedDateString = getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE)?.start_date;

  const selectedDate = new Date(storedDateString);

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  const formattedDate = selectedDate ? formatDateToInput(selectedDate) : '';

  const lastPage = getObjectFromSessionStorage(LAST_PAGE_NAME);

  const isEditMode = lastPage && lastPage === "Summary";

  const isProccedEnabled = !isButtonEnabled;

  const storedQuoteDateString = getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE)?.quote_timestamp;

  const quoteDate = new Date(storedQuoteDateString);

  const isPolicyStartDateWithin90Days = (newDate: Date) => {
    return differenceInCalendarDays(newDate, quoteDate) <= 90;
  };

  const policyStartDateMsgEnabled =  !isPolicyStartDateWithin90Days(new Date());

  function formatDateToInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // Format: yyyy-MM-dd
  }

  const handleComplete = async () => {
    try {
      const homeQuoteSelected = getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE);
      const quoteReference: string = homeQuoteSelected?.quote_reference ?? '';
      const patchRequest: UpdateStatusRequestObject = {
        quote_reference: quoteReference
      };
      const updateStatusResponse = await makeRequest({
        url: `${basePath}/home${HOME_ENDPOINT}/update_bind_status`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        payload: patchRequest
      });
      if (updateStatusResponse?.payment_channel === "APPLIED") {
        history.push({ pathname: `${routes.wallet.url}` });
      } else {
        console.error("Failed to update bind status.");
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const handleEditQuote = () => {
    history.push(`${routes.getQuotePage.url}${ABOUT_YOU}`);
  }

  const callLambdaApi = async () => {
    const selectedData = getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE);
    if(selectedData.quote_policy_holder_details!==null && selectedData.quote_policy_holder_details!==undefined){
      const fName = selectedData.quote_policy_holder_details.first_name;
      const lName = selectedData.quote_policy_holder_details.last_name;
      const fullName =`${fName} ${lName}`;
      const sendEmailUrl = process.env.REACT_APP_GATEWAY_ENDPOINT || '';
      fetch(`${sendEmailUrl}/email-stage/sendMail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerEmail: selectedData.quote_policy_holder_details.email,
          customerName: fullName
        }),
      })
        .then((response: Response)   => response.json())
        .catch((error: Error) => console.error('Error:', error));
    }
  };

  const goBacktoSummary = () => {
    history.push(routes.quoteSummaryPage.url);
  }
  const [policyStartDate, setPolicyStartDate] = useState(formattedDate);

  const updatePolicyStartDate = (field: string, value: string) => {
    setPolicyStartDate(value);
    setIsButtonEnabled(formattedDate !== value);
  };

  function shouldButtonDisabled(): boolean {
    return (agentScriptPending && buttonType === 'proceed') || !isProccedEnabled || policyStartDateMsgEnabled;
  }

  function insurSmartEnabledAndIsAgent(): boolean {
    return feature?.flags?.insure_smart_enabled === true && isAgent();
  }

  return (
    <div className={`${className}`}>
      <div
        className={`${commonPageStylesIdentifier} ${CommonPageStylesDesktopBackButton}`}
      >
        <MobileBackButton history={history} />
      </div>
      <ProgressBar
        stage={JOURNEY_YOUR_QUOTE}
        labelText={values.renewal ? RENEWAL_JOURNEY_LABEL_TEXT : HOME_JOURNEY}
        stages={values.renewal ? STAGES_RENEWAL_JOURNEY : STAGES_HOME_JOURNEY}
      />
      <div className={`${commonPageStylesIdentifier}__pageTitle`}>
        <TitleWithUnderLine>
          {values.renewal ? 'Your Summary' : 'Your Quote'}
        </TitleWithUnderLine>
      </div>
      <div className={`${className}__card`}>
        {isAgent() ? agentScriptingText() : null}
        <div className={`${className}__contentContainer`}>
          <div
            className={`${className}__quoteInfoContainer ${className}__paddedContainer`}
          >
            <div className={`${className}__logo`}>
              <ViewQuoteLogo />
            </div>
            <div className={`${className}__underwrittenText`}>
              Underwritten by{' '}
              <img
                className={`${className}__${insurerCaption(getInsurer(insurerType)).toLowerCase()}Logo`}
                alt={`${insurerCaption(getInsurer(insurerType)).toLowerCase()} logo`}
                src={`/asset/${insurerType.toLowerCase()}_logo.jpg`}
              />
            </div>
            <div className={`${className}__quoteText`}>
              {alternativePolicy
                ? 'Complete your quote in 3 easy steps'
                : values.renewal
                  ? 'Complete your renewal in 3 easy steps'
                  : 'You do not have to purchase an optional extra in order to take out a policy'}
            </div>
            <div>
              <QuoteInfo {...getQuoteInfoProps()} />
            </div>
          </div>
          <Form>
            {!values.renewal && (
              <>
                {getQuoteExtras()}
                <div
                  className={`${commonFormStylesIdentifier}__fieldContainer ${className}__paddedContainer`}
                >
                  <label
                    htmlFor="excess"
                    className={`${commonFormStylesIdentifier}__fieldLabel`}
                  >
                    <AccordionText
                      label="Select your excess to suit your needs and requirements"
                      icon="info"
                      iconAlign="right"
                      customLabelClass={`${commonFormStylesIdentifier}__accordionTextFieldLabel`}
                    >
                      <div
                        className={`${commonFormStylesIdentifier}__infoText`}
                      >
                        <ul className={`${className}__bulletPoints`}>
                          <li>
                            An excess is the part of the claim cost that you
                            must pay. You are responsible for paying the excess
                            amounts shown in your schedule for certain claim
                            types under this policy.
                          </li>
                          <li>
                            If your policy is subject to any other form of
                            excess, this will be detailed in your Policy
                            Schedule or in the endorsements section of the
                            Policy Schedule.
                          </li>
                          <li>
                            You may also have the option of choosing a higher
                            excess for a lower premium, or a lower excess for a
                            higher premium. If you choose a higher excess, this
                            will increase the amount you need to pay if you have
                            a claim.
                          </li>
                          <li>
                            Terms and Conditions apply. See your{' '}
                            <ExternalLink
                              text="Policy Documents"
                              url={policyImportantDocuments}
                            />{' '}
                            for full details.
                          </li>
                        </ul>
                      </div>
                    </AccordionText>
                  </label>

                  <div
                    className={`${className}--fieldLabelSpacing ${className}--flexGroup`}
                  >
                    <ButtonGroup
                      error={isFieldError('excess')}
                      errorMessage={errors.excess}
                      name="excess"
                      disabled={checkIfPolicyExcessIsAllowed()}
                      onClick={(e: string) => {
                        handleChangeWRecalculate('excess', e);
                      }}
                      options={EXCESS}
                      spaceBetween={true}
                      selected={recalculatedExcess || values.excess}
                      onBlur={handleBlur}
                      value={recalculatedExcess || values.excess}
                    />
                  </div>
                </div>
              </>
            )}
            <div className={`${commonFormStylesIdentifier}__dividerContainer`}>
              <WideDivider height={1} color={dividerColor} />
            </div>
            <div className={`${className}__premiumBreakdownContainer`}>
              <HomePremiumBreakdown
                premiumDetails={premiumDetails}
                yourCoverObj={values.renewal ? coverDetails : yourCoverObj}
                coverType={
                  values.renewal
                    ? coverDetails?.coverType
                    : yourHomeObj.coverType
                }
                unspecifiedItemsValue={
                  values.renewal
                    ? coverDetails?.unspecifiedItemsValue
                    : allData?.yourCoverObj?.unspecifiedItems
                }
                specifiedItemsValue={
                  values.renewal
                    ? coverDetails?.specifiedItems
                    : allData?.yourCoverObj?.specifiedItems
                }
                totalPrice={price}
                monthlyPrice={monthlyPrice}
                insurer={InsuranceCodes[insurerType]}
                insurerType={insurerType}
                renewal={values.renewal}
                alternativePolicy={alternativePolicy}
                addonDetails={addonDetails}
                setFieldValue={setFieldValue}
                values={values}
                claims={claims}
                start_date={coverDetails.start_date}
                home_premium_discounts_enabled={
                  feature?.flags?.home_premium_discounts_enabled
                }
              />
            </div>
            <div className={`${commonFormStylesIdentifier}__dividerContainer`}>
              <WideDivider height={1} color={dividerColor} />
            </div>
            {insurSmartEnabledAndIsAgent() && (
              <div className={`${className}__switchContainer`}>
                <label
                  htmlFor="insurSmart"
                  className={`${commonFormStylesIdentifier}__fieldLabel ${commonFormStylesIdentifier}--withNoMargin`}
                >
                  InsurSmart Customer
                </label>
                <SimpleSwitch
                  name="insurSmartSwitch"
                  on={insurSmart}
                  isYesNoQuestion={true}
                  onClick={() => {
                    if (!insurSmartAgentScriptViewed) {
                      setAgentScriptPending(true);
                      setInsurSmartAgentScriptViewed(true);
                    }  else {
                      setAgentScriptPending(false);
                      setInsurSmartAgentScriptViewed(agentScriptPending ? false : true);
                    }
                    setInsurSmart(!insurSmart);
                    setFieldValue('insurSmartSwitch', !insurSmart);
                    handleChange();
                  }}
                  disabled={!insurSmart && agentScriptPending}
                />
              </div>
            )}
            {insurSmart && (
              <div>
                <label
                  htmlFor="mortgageApplicationNumber"
                  className={`${commonFormStylesIdentifier}__fieldLabel`}
                >
                  Mortgage Application Number
                </label>
                <InputField
                  name="mortgageApplicationNumber"
                  error={isFieldError('mortgageApplicationNumber')}
                  errorMessage={errors.mortgageApplicationNumber}
                  value={values.mortgageApplicationNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={8}
                />
                {isEditMode && (
                  <div>
                    <label
                      htmlFor="policyStart"
                      className={`${commonFormStylesIdentifier}__fieldLabel`}
                    >Policy Start Date
                    </label>
                    {isButtonEnabled && (
                      <p style={{color:'red'}}>**{insurSmartStartDateChange}</p>
                    )}
                    { policyStartDateMsgEnabled && (<p style={{color:'red'}}>Original Quote is over 90 days old.  Please use &quot;Edit Quote&quot; button to recalculate with new Policy Start Date.</p>
                    )}
                    <DatePicker
                      error={errors.policyStart}
                      name="policyStart"
                      onBlur={handleBlur}
                      onChange={(e: SyntheticEvent<HTMLInputElement>) => {
                        updatePolicyStartDate(
                          'startDate',
                          e.currentTarget.value
                        );
                      }}
                      placeholder="Type here"
                      touched={policyStartDate}
                      value={policyStartDate}
                      minDate={getCurrentDate()}
                      maxDate={addDaysToDate(getCurrentDate(), 90)}
                    />
                    <button
                      onClick={handleComplete}
                      disabled={!isButtonEnabled}
                      style={{
                        marginTop: '10px',
                        backgroundColor: isButtonEnabled ? '#0404FC' : '#ccc',
                        color: 'white',
                        padding: '10px 20px',
                        border: 'none',
                        cursor: selectedDate ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Complete
                    </button>
                    {insurSmartQuote && (
                      <button
                        onClick={handleEditQuote}
                        style={{
                          marginTop: '10px',
                          backgroundColor: '#0404FC',
                          color: 'white',
                          padding: '10px 20px',
                          border: 'none',
                          float: 'right',
                          cursor: 'pointer'
                        }}
                      >
                        Edit Quote
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </Form>
        </div>
        <div className={`${className}__paddedContainer`}>
          {values.renewal ? (
            <>
              {(values.autoRenewal === false ||
                alternativePolicy ||
                !getObjectFromSessionStorage(SELECTED_HOME_QUOTE_STORAGE)
                  ?.direct_debit_policy) && (
                <TermsAndConditionsCheckbox
                  setFieldValue={setFieldValue}
                  values={values}
                  showStatementOfFact={true}
                />
              )}
            </>
          ) : insurSmartTrue() ? (
            <div className={`${className}__insurSmart`}>
              <div className={`${className}__eligibilityWarningText`}>
                <h3>{insurSmartTitle}</h3>
                {insurSmartTsAndCs}
                {insurSmartDisplayError && (
                  <label className={`${className}__insurSmart__error`}>
                    {insurSmartErrorMsg}
                  </label>
                )}
                <p className="proceed-text"><strong>{insurSmartProcced}</strong></p>
                <Checkbox
                  id="insurSmartCheckBox"
                  name="insurSmartCheckBox"
                  labelFontSize={16}
                  label={insurSmartCheckText}
                  isChecked={insurSmartChecked}
                  disabled={agentScriptPending}
                  onChange={() => {
                    setFieldValue(
                      'insurSmartCheckBox',
                      !insurSmartChecked
                    );
                    setInsurSmartChecked(!insurSmartChecked);
                    if (insurSmartChecked === true) {
                      setInsurSmartDisplayError(false);
                    }
                  }}
                  value={insurSmartChecked}
                />
              </div>
            </div>
          ) : (
            <>
              <h1 className={`${className}__eligibilityWarningTitle`}>
                Before You Proceed
              </h1>
              <div className={`${className}__eligibilityWarningText`}>
                <p>
                  {`You are responsible for confirming your eligibility for our cover and that the cover you have chosen meets your particular needs. Before deciding to
                      purchase it is important that you have  read and understood your premium breakdown above.`}
                  <br />
                  {`This quote is underwritten by ${getCompanyName()}. Quotes are valid for 90 days only provided all information is correct and does not change.`}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      <QuoteDisplay
        quoteReference={quoteReference}
        price={getQuoteTotal(premiumDetails, insurerType)}
        onClickFunction={onProceedClicked}
        sticky
        disabled={!isValid || shouldButtonDisabled()}
        buttonType={buttonType}
        recalculate={recalculateHomeQuote}
        callStatus={requestStatus}
        insuranceType={HOME_INSURANCE_TYPE}
        title="Annual Premium"
        insurerType={insurerType}
        insurSmartQuote={insurSmartQuote && insurSmart}
        goBack={goBacktoSummary}
      />
    </div>
  );
};

const initialFormValues = {
  excess: '3',
  accidentalDamage: false,
  ncdProtectionPlus: false,
  homeEmergencyAssist: false,
  acceptedTermsAndConditions: undefined,
  claimsConditionTwo: undefined,
  claimsConditionOne: undefined,
  insurSmartCheckBox: false,
  insurSmartSwitch: false,
  autoRenewal: undefined,
  renewal: false,
  mortgageApplicationNumber: undefined
};

const isInitialFormValid = () => {
  return viewQuoteYupSchema.isValidSync(initialFormValues);
};

const FormikViewQuote = withFormik<Props, ViewQuoteValues>({
  mapPropsToValues(): {} {
    return initialFormValues;
  },
  handleSubmit(values: ViewQuoteValues, { setSubmitting }: FormikProps) {
    setSubmitting(false);
  },
  isInitialValid: isInitialFormValid(),
  validationSchema: () => {
    return viewQuoteYupSchema;
  },
  displayName: 'ViewQuote'
})(ViewQuote);

export default FormikViewQuote;
